import { useState, useEffect, useCallback } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { getTaxInvoiceMappings } from '~apis/mapping';
import {
  EmailResendRequest,
  InvoiceIssuanceCountRequest,
  InvoiceIssuanceListRequest,
  InvoiceIssuanceRequest,
  InvoiceModificationRequest,
  RetryEmailSendRequest,
  TaxInvoiceQueryParams,
  cancelIssuance,
  createTaxInvoiceMemo,
  getInvoiceIssuanceCount,
  getInvoiceIssuanceExistModificationInfo,
  getInvoiceIssuanceNewModificationInfo,
  getInvoiceIssuances,
  getIssuanceByApprovalNo,
  getIssueConnectStatus,
  getTaxInvoice,
  getTaxInvoices,
  getTaxInvoicesByCursor,
  issueTaxInvoice,
  modifyTaxInvoice,
  resendEmail,
  retryEmailSend,
  syncInvoiceDetail,
  uploadExcelFiles,
  validateExcelFiles,
} from '~apis/tax-invoice';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { queryClient } from '~libs/queryClient';
import { TaxInvoiceRequest, TaxInvoiceType, TaxInvoice } from '~models/tax-invoice';
import { mixpanel } from '~pages/_app';

export const TaxInvoiceQuery = {
  default: () => ['taxInvoice'],
  mapping: ({
    companyId,
    taxInvoiceId,
    type,
  }: {
    companyId: string;
    taxInvoiceId: string;
    type: TaxInvoiceType;
  }) => ['taxInvoiceMappings', companyId, taxInvoiceId],
};

export const IssueQuery = {
  default: () => ['issue'],
  detail: ({ companyId, invoiceIssuanceId }: { companyId: string; invoiceIssuanceId: number }) => [
    'issueDetail',
    companyId,
    invoiceIssuanceId,
  ],
  approvalNo: ({ companyId, approvalNo }: { companyId: string; approvalNo: string }) => [
    'issueApprovalNo',
    companyId,
    approvalNo,
  ],
  count: ({ companyId, start, end }: { companyId: string; start: string; end: string }) => [
    'issueCount',
    companyId,
    start,
    end,
  ],
};

export function useTaxInvoices(request: TaxInvoiceRequest) {
  return useQuery({
    queryKey: [
      ...TaxInvoiceQuery.default(),
      request.companyId,
      request.startDate,
      request.endDate,
      request.type,
      request.page,
      request.sort,
      request.size,
      request.searchParam,
      request.businessEntityId,
      request.taxationType,
      request.modificationType,
    ],
    queryFn: () => getTaxInvoices(request),
    enabled: Boolean(request.companyId),
  });
}

export function useTaxInvoiceIssues({
  companyId,
  request,
}: {
  companyId: string;
  request?: Partial<InvoiceIssuanceListRequest>;
}) {
  return useQuery({
    refetchOnMount: true,
    queryKey: [
      ...IssueQuery.default(),
      companyId,
      request?.page,
      request?.size,
      request?.sort,
      request?.status,
      request?.start,
      request?.end,
      request?.isModification,
      request?.taxationType,
      request?.keyword,
      request?.writerId,
      request?.sort,
    ],
    queryFn: () =>
      getInvoiceIssuances({
        companyId,
        request,
      }),
    enabled: Boolean(companyId),
  });
}

export function useTaxInvoice(
  companyId: string,
  type: TaxInvoiceType,
  id: string,
  trigger?: boolean,
) {
  return useQuery({
    queryKey: ['taxInvoice', companyId, type, id],
    queryFn: () => getTaxInvoice({ companyId, type, id }),
    enabled: Boolean(companyId) && Boolean(type) && Boolean(id) && (trigger ?? true),
  });
}

export function useCreateTaxInvoiceMemo() {
  return useMutation({
    mutationFn: (variables: {
      companyId: string;
      invoiceId: string;
      memo: string;
      taxInvoiceType: TaxInvoiceType;
    }) => createTaxInvoiceMemo(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([...TaxInvoiceQuery.default(), variables.companyId]);
    },
  });
}

export function useGetIssueConnectStatus(companyId: string) {
  return useQuery({
    queryKey: [...IssueQuery.default(), companyId, 'status'],
    queryFn: () => getIssueConnectStatus({ companyId }),
    enabled: Boolean(companyId),
  });
}

export function useIssueTaxInvoice() {
  return useMutation({
    mutationFn: (variables: { companyId: string; request: InvoiceIssuanceRequest }) =>
      issueTaxInvoice(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([...IssueQuery.default(), variables.companyId]);
    },
  });
}

export function useIssueModifiedTaxInvoice() {
  return useMutation({
    mutationFn: (variables: { companyId: string; request: InvoiceModificationRequest }) =>
      modifyTaxInvoice(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([...IssueQuery.default(), variables.companyId]);
    },
  });
}

export function useCancelIssuance() {
  return useMutation({
    mutationFn: (variables: { companyId: string; invoiceIssuanceId: number }) =>
      cancelIssuance(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([...IssueQuery.default(), variables.companyId]);
    },
  });
}

export function useSyncInvoiceDetail() {
  return useMutation({
    mutationFn: (variables: { companyId: string; approvalNo: string; type?: TaxInvoiceType }) =>
      syncInvoiceDetail({
        companyId: variables.companyId,
        approvalNo: variables.approvalNo,
        type: variables.type,
      }),
  });
}

export function useValidateFiles() {
  return useMutation({
    mutationFn: (variables: { companyId: string; file: File }) =>
      validateExcelFiles(variables.companyId, variables.file),
  });
}

export function useUploadFiles() {
  return useMutation({
    mutationFn: (variables: { companyId: string; file: File }) =>
      uploadExcelFiles(variables.companyId, variables.file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([...IssueQuery.default(), variables.companyId]);
    },
  });
}

export function useGetInvoiceIssuanceByApprovalNo({
  companyId,
  approvalNo,
  hasDetail,
}: {
  companyId: string;
  approvalNo: string;
  hasDetail?: boolean;
}) {
  return useQuery({
    queryKey: [
      ...IssueQuery.approvalNo({
        companyId,
        approvalNo,
      }),
      companyId,
    ],
    queryFn: () => getIssuanceByApprovalNo({ companyId, approvalNo }),
    enabled: Boolean(companyId) && Boolean(approvalNo) && Boolean(hasDetail),
  });
}

export function useGetInvoiceIssuanceInfo({
  companyId,
  invoiceIssuanceId,
}: {
  companyId: string;
  invoiceIssuanceId: number;
}) {
  return useQuery({
    queryKey: [
      ...IssueQuery.detail({
        companyId,
        invoiceIssuanceId,
      }),
      companyId,
    ],
    queryFn: () => getInvoiceIssuanceNewModificationInfo({ companyId, invoiceIssuanceId }),
    enabled: Boolean(companyId) && Boolean(invoiceIssuanceId),
    refetchOnMount: true,
  });
}

export function useGetInvoiceExistIssuanceInfo({
  companyId,
  invoiceIssuanceId,
  hasDetail,
}: {
  companyId: string;
  invoiceIssuanceId: number;
  hasDetail?: boolean;
}) {
  return useQuery({
    queryKey: [
      ...IssueQuery.detail({
        companyId,
        invoiceIssuanceId,
      }),
      companyId,
    ],
    refetchOnMount: true,
    queryFn: () => getInvoiceIssuanceExistModificationInfo({ companyId, invoiceIssuanceId }),
    enabled: Boolean(companyId) && Boolean(invoiceIssuanceId) && Boolean(hasDetail),
  });
}

export function useGetInvoiceIssuanceCount(data: InvoiceIssuanceCountRequest) {
  return useQuery({
    queryKey: [...IssueQuery.count({ ...data })],
    queryFn: () => getInvoiceIssuanceCount({ ...data }),
    enabled: !!data.companyId,
  });
}

export function useGetTaxInvoiceMappingsById({
  companyId,
  taxInvoiceId,
  type,
}: {
  companyId: string;
  taxInvoiceId: string;
  type: TaxInvoiceType;
}) {
  return useQuery({
    queryKey: [...TaxInvoiceQuery.mapping({ companyId, taxInvoiceId, type })],
    queryFn: () => getTaxInvoiceMappings({ companyId, taxInvoiceId, type }),
    enabled: Boolean(companyId) && Boolean(taxInvoiceId) && Boolean(type),
    refetchOnMount: true,
  });
}

export function useResendEmail() {
  return useMutation({
    mutationFn: (variables: EmailResendRequest) => resendEmail(variables),
  });
}

export function useRetryEmailSend() {
  return useMutation({
    mutationFn: (variables: RetryEmailSendRequest) => retryEmailSend(variables),
  });
}

export function useInfiniteTaxInvoicesByCursor({
  companyId,
  startDate,
  endDate,
  type,
  size = 20,
  withNoDate,
}: {
  companyId: string;
  startDate: string;
  endDate: string;
  type?: TaxInvoiceType;
  size?: number;
  withNoDate?: boolean;
}) {
  const [allTaxInvoices, setAllTaxInvoices] = useState<TaxInvoice[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<{
    transactionAt?: string;
    reportingDate?: string;
    id: number;
  } | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  // 초기 데이터 로드
  const initialQuery = useQuery({
    queryKey: [...TaxInvoiceQuery.default(), 'infinite', companyId, startDate, endDate, type, size],
    queryFn: () => {
      const queryParams: TaxInvoiceQueryParams = {
        startDate,
        endDate,
        type,
        size,
        sortOrder: 'LATEST_FIRST',
      };
      return getTaxInvoicesByCursor({ companyId, queryParams });
    },
    refetchOnMount: true,
    enabled: !!companyId,
  });

  // 초기 데이터가 로드되면 상태 설정
  useEffect(() => {
    if (initialQuery.data?.Data?.data) {
      const data = initialQuery.data.Data.data;
      setAllTaxInvoices(data.content);
      setNextCursor(data.nextCursor || null);
      setHasNext(data.hasNext);
    }
  }, [initialQuery.data]);

  const loadMore = useCallback(async () => {
    if (!companyId || !nextCursor || !hasNext || isLoadingMore) return;

    mixpanel.track(MIXPANEL_EVENT.MOBILE.SCROLL_LOAD_MORE, {
      direction: 'up',
    });
    setIsLoadingMore(true);
    try {
      const queryParams: TaxInvoiceQueryParams = {
        startDate: withNoDate ? undefined : startDate,
        endDate: withNoDate ? undefined : endDate,
        type,
        size,
        sortOrder: 'LATEST_FIRST',
        // transactionAt 또는 reportingDate 중 있는 것을 사용
        cursorReportingDate: nextCursor.reportingDate,
        cursorId: nextCursor.id.toString(),
      };

      const response = await getTaxInvoicesByCursor({ companyId, queryParams });

      if (response?.Data?.data) {
        const data = response.Data.data;
        setAllTaxInvoices((prev) => [...prev, ...data.content]);
        setNextCursor(data.nextCursor || null);
        setHasNext(data.hasNext);
      }
    } catch (error) {
      console.error('Failed to load more tax invoices:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [companyId, nextCursor, hasNext, isLoadingMore, startDate, endDate, type, size]);

  // 쿼리 파라미터가 변경되면 모든 상태 리셋
  useEffect(() => {
    setAllTaxInvoices([]);
    setNextCursor(null);
    setIsLoadingMore(false);
    setHasNext(false);
    setTotalAmount(0);
  }, [startDate, endDate, type, size]);

  return {
    data: allTaxInvoices,
    isLoading: initialQuery.isFetching,
    isLoadingMore,
    hasNext,
    loadMore,
    totalAmount,
    error: initialQuery.error,
  };
}
